import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Download, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '@/context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TrackApplication = () => {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleSearch = async () => {
    if (!email) {
      toast.error(t.apply.emailPlaceholder);
      return;
    }

    setLoading(true);
    try {
      // SECURITY: Pass email verification parameter
      const response = await axios.get(`${API}/applications/by-email/${encodeURIComponent(email)}?verify_email=${encodeURIComponent(email)}`);
      setApplications(response.data);
      setSearched(true);
      
      if (response.data.length === 0) {
        toast.info(t.track.noApplications);
      }
    } catch (error) {
      console.error('Search error:', error);
      if (error.response?.status === 403) {
        toast.error('Email verification failed');
      } else {
        toast.error(t.common.error);
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-5 h-5" />;
      case 'processing':
        return <AlertCircle className="w-5 h-5" />;
      case 'approved':
        return <CheckCircle className="w-5 h-5" />;
      case 'rejected':
        return <XCircle className="w-5 h-5" />;
      default:
        return <Clock className="w-5 h-5" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'processing':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'approved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  const handleDownloadVisa = (url) => {
    window.open(`${BACKEND_URL}${url}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] py-12" data-testid="track-application-page">
      <div className="container mx-auto px-6 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 text-center"
        >
          <h1 className="text-4xl font-heading font-bold text-[#1A4D7F] mb-3">
            {t.track.title}
          </h1>
          <p className="text-slate-600">{t.track.subtitle}</p>
        </motion.div>

        <Card className="border-slate-200 shadow-lg mb-8">
          <CardHeader>
            <CardTitle className="text-xl font-heading">{t.track.search || 'Search Applications'}</CardTitle>
            <CardDescription>{t.track.subtitle}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <div className="flex-1">
                <Label htmlFor="search-email" className="sr-only">{t.apply.email}</Label>
                <Input
                  id="search-email"
                  type="email"
                  placeholder={t.track.emailPlaceholder}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="h-12"
                  data-testid="email-search-input"
                />
              </div>
              <Button
                onClick={handleSearch}
                disabled={loading}
                className="bg-[#1A4D7F] hover:bg-[#153e66] h-12 px-8"
                data-testid="search-button"
              >
                {loading ? t.track.searching : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    {t.track.search}
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {searched && applications.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-10 h-10 text-slate-400" />
            </div>
            <p className="text-slate-600 text-lg">{t.track.noApplications}</p>
          </motion.div>
        )}

        {applications.length > 0 && (
          <div className="space-y-6" data-testid="applications-list">
            {applications.map((app, index) => (
              <motion.div
                key={app.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                data-testid={`application-card-${index}`}
              >
                <Card className="border-l-4 border-l-[#009FB7] hover:shadow-lg transition-all">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg font-heading">
                          {t.track.applicationId}: {app.id.substring(0, 8)}
                        </CardTitle>
                        <CardDescription className="mt-1">
                          {t.track.submittedOn} {new Date(app.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </CardDescription>
                      </div>
                      <Badge
                        variant="outline"
                        className={`${getStatusColor(app.status)} flex items-center gap-2 px-3 py-1`}
                        data-testid={`status-badge-${app.status}`}
                      >
                        {getStatusIcon(app.status)}
                        {app.status.toUpperCase()}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                      <div>
                        <p className="text-sm text-slate-500">{t.track.applicantName}</p>
                        <p className="font-medium">{app.first_name} {app.last_name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">{t.track.passportNumber}</p>
                        <p className="font-medium">{app.passport_number}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">{t.track.visaType}</p>
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                          {app.visa_duration === '60_days' ? `60 ${t.common.days}` : `30 ${t.common.days}`}
                        </Badge>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">{t.track.nationality}</p>
                        <p className="font-medium">{app.nationality || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">{t.track.paymentStatus}</p>
                        <Badge
                          variant="outline"
                          className={app.payment_status === 'paid' ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}
                        >
                          {app.payment_status === 'paid' ? t.track.paid : t.track.pending}
                        </Badge>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">{t.track.totalFee}</p>
                        <p className="font-medium">{app.total_visa_fee || 'N/A'} AED</p>
                      </div>
                    </div>

                    {app.admin_notes && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                        <p className="text-sm font-medium text-blue-900 mb-1">{t.track.adminNotes}:</p>
                        <p className="text-sm text-blue-800">{app.admin_notes}</p>
                      </div>
                    )}

                    {/* Main Applicant Visa */}
                    {app.visa_document_url && (
                      <div className="mb-4">
                        <p className="text-sm font-medium text-slate-700 mb-2">{t.track.downloadVisa}:</p>
                        <Button
                          onClick={() => handleDownloadVisa(app.visa_document_url)}
                          className="bg-[#009FB7] hover:bg-[#008fa5] w-full"
                          data-testid="download-visa-button"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          {t.track.downloadVisa} - {app.first_name} {app.last_name}
                        </Button>
                      </div>
                    )}

                    {/* Family Members Visas */}
                    {app.family_members && app.family_members.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-slate-700 mb-2">{t.track.familyMemberVisas}:</p>
                        <div className="space-y-2">
                          {app.family_members.map((member, index) => (
                            <div key={index}>
                              {member.visa_document_url ? (
                                <Button
                                  onClick={() => handleDownloadVisa(member.visa_document_url)}
                                  variant="outline"
                                  className="w-full justify-start"
                                  size="sm"
                                >
                                  <Download className="w-4 h-4 mr-2" />
                                  {t.track.downloadVisa} - {member.first_name} {member.last_name}
                                </Button>
                              ) : (
                                <div className="text-sm text-slate-500 p-2 border border-slate-200 rounded">
                                  {member.first_name} {member.last_name} - {t.track.pending}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackApplication;
